import type {DescriptorToField, InletDescriptor, NodeClassDescriptor, OutletDescriptor, ValueTypeDescriptor} from "#template/runtime-graph/descriptors"
import type {BuilderInlet, BuilderOutlet, GraphBuilder} from "#template/runtime-graph/graph-builder"
import {isBuilderOutlet} from "#template/runtime-graph/descriptors"
import {And} from "#template/runtime-graph/nodes/and"
import {Branch} from "#template/runtime-graph/nodes/branch"
import {makeEntriesToMapClass} from "#template/runtime-graph/nodes/entries-to-map"
import {Filter} from "#template/runtime-graph/nodes/filter"
import {Gate} from "#template/runtime-graph/nodes/gate"
import {GetField} from "#template/runtime-graph/nodes/get-field"
import {Group} from "#template/runtime-graph/nodes/group"
import {Lambda} from "#template/runtime-graph/nodes/lambda"
import {List} from "#template/runtime-graph/nodes/list"
import {Merge} from "#template/runtime-graph/nodes/merge"
import {Phi} from "#template/runtime-graph/nodes/phi"
import {generatePureLambda} from "#template/runtime-graph/nodes/pure-lambda"
import {SparseList} from "#template/runtime-graph/nodes/sparse-list"
import {State} from "#template/runtime-graph/nodes/state"
import {Struct} from "#template/runtime-graph/nodes/struct"
import {Tap} from "#template/runtime-graph/nodes/tap"
import {Trace} from "#template/runtime-graph/nodes/trace"
import {makeValueNodeClass} from "#template/runtime-graph/nodes/value"
import type {NotReady} from "#template/runtime-graph/slots"
import type {ConnectionData, NodeClass, NodeId, Unresolved} from "#template/runtime-graph/types"
import {ResolveAlias, ResolveId} from "#template/runtime-graph/types"
import {UnresolvedToNull} from "#template/runtime-graph/nodes/unresolved-to-null"
import {MapOverList} from "#template/runtime-graph/nodes/map-over-list"
import type {WaitForFinishedModifyingSceneDescriptor} from "#template/runtime-graph/nodes/wait-for-finished-modifying-scene"
import {WaitForFinishedModifyingScene} from "#template/runtime-graph/nodes/wait-for-finished-modifying-scene"
import type {ISceneManager} from "#template/interfaces/scene-manager"

type InletKeys<D extends NodeClassDescriptor> = {[K in keyof D]: D[K] extends InletDescriptor<infer T> ? K : never}[keyof D]
type OutletKeys<D extends NodeClassDescriptor> = {[K in keyof D]: D[K] extends OutletDescriptor<infer T> ? K : never}[keyof D]

type InletsForDescriptor<D extends NodeClassDescriptor> = {[K in InletKeys<D>]: DescriptorToField<D[K]>}
type OutletsForDescriptor<D extends NodeClassDescriptor> = {[K in OutletKeys<D>]: DescriptorToField<D[K]>}

export class GraphBuilderScope {
    private idCounter = 1
    private uniqueIdSet = new Set<NodeId>()

    constructor(
        private builder: GraphBuilder,
        private prefix: string,
    ) {}

    getPrefix(): string {
        return this.prefix
    }

    private fullId(id: string): NodeId {
        return `${this.prefix}:${id}` // use ':' to avoid conflict between namespace prefixes and node IDs
    }

    private genLocalId(typeName: string): NodeId {
        return `${typeName}@${this.idCounter++}` // NOTE: type name must be included, so that node instances can be matched!
    }

    private checkOrGenLocalId(localId: string | undefined, typeName: string): string {
        return localId != null ? this.ensureUnique(`${typeName}(${localId})`) : this.genLocalId(typeName)
    }

    private ensureUnique(uniqueId: NodeId) {
        if (this.uniqueIdSet.has(uniqueId)) {
            throw Error(`Runtime node ID is not unique: ${uniqueId}`)
        }
        this.uniqueIdSet.add(uniqueId)
        return uniqueId
    }

    genNodeId<D extends NodeClassDescriptor>(nodeClass: NodeClass<D>): NodeId {
        return this.fullId(this.genLocalId(nodeClass.uniqueName))
    }

    genStructId(typeName: string): NodeId {
        return this.fullId(this.genLocalId(typeName))
    }

    debug<T>(outletOrNode: T): T {
        if (isBuilderOutlet(outletOrNode)) {
            ;(outletOrNode.node as any).$debug = true
        } else if (typeof outletOrNode === "object" && outletOrNode !== null) {
            for (const value of Object.values(outletOrNode)) {
                if (isBuilderOutlet(value)) {
                    ;(value.node as any).$debug = true
                    break
                }
            }
        }
        return outletOrNode
    }

    alias<T>(val: BuilderInlet<T>, id: string): void {
        this.builder.aliases.set(id, val)
    }

    resolve<T>(id: string): ResolveAlias<T> | BuilderOutlet<typeof Unresolved> {
        return new ResolveAlias<T>(id)
    }

    scope(localId?: string): GraphBuilderScope {
        localId = this.checkOrGenLocalId(localId, "Scope")
        return new GraphBuilderScope(this.builder, `${this.prefix}/${localId}`)
    }

    state<T>(stateClass: {new (): T}, uniqueId: string): BuilderOutlet<T> {
        return this._node(State, {
            $id: this.fullId(this.ensureUnique(`State(${uniqueId})`)),
            stateClass,
        }).output
    }

    node<D extends NodeClassDescriptor>(nodeClass: NodeClass<D>, properties?: {$id?: NodeId} & InletsForDescriptor<D>): OutletsForDescriptor<D> {
        return this._node(nodeClass, properties)
    }

    private clearInletAssignments(connectionData: ConnectionData): void {
        const removeKeys: string[] = []
        for (const [key, value] of Object.entries(connectionData)) {
            if (key === "$info" || key === "$id" || key === "$debug") continue
            else if (isBuilderOutlet(value) && value.node === connectionData) continue
            removeKeys.push(key)
        }
        for (const key of removeKeys) {
            delete (connectionData as any)[key]
        }
    }

    private _node<D extends NodeClassDescriptor>(
        nodeClass: NodeClass<D>,
        properties?: {$id?: NodeId} & InletsForDescriptor<D>,
    ): InletsForDescriptor<D> & OutletsForDescriptor<D> {
        const id = properties?.$id ?? this.genNodeId(nodeClass)
        let connectionData = this.builder.connectionData.get(id)
        if (!connectionData) {
            connectionData = {
                $info: {
                    id,
                    nodeClass,
                },
            }
            for (const key in nodeClass.descriptor) {
                const propDesc = nodeClass.descriptor[key]
                if (propDesc.descType === "outlet") {
                    ;(connectionData as any)[key] = {
                        outlet: true,
                        node: connectionData,
                        key,
                        inst: undefined!,
                        valueTypeDesc: propDesc.valueTypeDesc,
                    } as BuilderOutlet<unknown>
                } else if (propDesc.descType === "scope") {
                    connectionData.$info.provideScopeAs = key
                }
            }
            this.builder.connectionData.set(id, connectionData)
        } else {
            if (connectionData.$info.nodeClass !== nodeClass) {
                // The same node id was built from a different node class across rebuilds, which must not happen. For now, warn only.
                this.builder.logger.error("Runtime-graph node id reused for a different node class", {
                    id,
                    stored: connectionData.$info.nodeClass.uniqueName,
                    requested: nodeClass.uniqueName,
                })
            }
            this.clearInletAssignments(connectionData)
        }
        // disconnect all inlets
        for (const key in nodeClass.descriptor) {
            const propDesc = nodeClass.descriptor[key]
            if (propDesc.descType === "inlet") {
                ;(connectionData as any)[key] = undefined
            }
        }
        //console.log("connectionData", connectionData)
        this.builder.unvisitedNodes.delete(id)
        if (properties) {
            for (const [k, v] of Object.entries(properties)) {
                if (k !== "$id") {
                    ;(connectionData as any)[k] = v
                }
            }
        }
        return connectionData as any
    }

    private _struct<T>(typeName: string, properties?: {$id?: NodeId} & {[K in keyof T]?: BuilderInlet<T[K]> | (T[K] extends NodeId ? symbol : never)}) {
        // structs require all fields to be defined before emitting output
        const id = properties?.$id ?? this.genStructId(typeName)
        let connectionData = this.builder.connectionData.get(id)
        if (!connectionData) {
            connectionData = {
                $info: {
                    id,
                    nodeClass: Struct,
                },
            }
            ;(connectionData as any).__output_value = {outlet: true, node: connectionData, key: "__output_value"}
            this.builder.connectionData.set(id, connectionData)
        } else {
            this.clearInletAssignments(connectionData)
        }
        this.builder.unvisitedNodes.delete(id)
        if (properties) {
            for (let [k, v] of Object.entries(properties)) {
                if ((v as any) === ResolveId) {
                    v = id
                }
                if (k !== "$id") {
                    ;(connectionData as any)[k] = v
                }
            }
        }
        return connectionData as unknown as {__output_value: BuilderOutlet<T>} & {[K in keyof T]: BuilderInlet<T[K]>}
    }

    struct<T>(typeName: string, properties: {$id?: NodeId} & {[K in keyof T]: BuilderInlet<T[K]> | (T[K] extends NodeId ? symbol : never)}): BuilderOutlet<T> {
        return this._struct<T>(typeName, properties).__output_value
    }

    group<T>(
        typeName: string,
        properties?: {$id?: NodeId} & {[K in keyof T]?: BuilderInlet<T[K]> | (T[K] extends NodeId ? symbol : never)},
    ): BuilderOutlet<{[K in keyof T]: T[K] | typeof NotReady}> {
        // groups do _not_ require all fields to be defined before emitting output
        const id = properties?.$id ?? this.genStructId(typeName)
        let connectionData = this.builder.connectionData.get(id)
        if (!connectionData) {
            connectionData = {
                $info: {
                    id,
                    nodeClass: Group,
                },
            }
            ;(connectionData as any).__output_value = {outlet: true, node: connectionData, key: "__output_value"}
            this.builder.connectionData.set(id, connectionData)
        } else {
            this.clearInletAssignments(connectionData)
        }
        //console.log("connectionData", connectionData);
        this.builder.unvisitedNodes.delete(id)
        if (properties) {
            for (let [k, v] of Object.entries(properties)) {
                if ((v as any) === ResolveId) {
                    v = id
                }
                if (k !== "$id") {
                    ;(connectionData as any)[k] = v
                }
            }
        }
        return (connectionData as any).__output_value
    }

    record<K extends string | number, V>(d: Record<K, BuilderInlet<V>>, localId?: string): BuilderOutlet<Record<K, V>> {
        const id = this.fullId(this.checkOrGenLocalId(localId, "Record"))
        const node = this._struct<Record<K, V>>("Record", {$id: id} as any)
        for (const k in d) {
            ;(node as any)[k] = d[k] //TODO: StructNode does not work with Record type ??
        }
        return node.__output_value
    }

    get<T, K extends keyof T>(x: BuilderInlet<T>, k: BuilderInlet<K>): BuilderOutlet<T[K]>
    get<T, K extends keyof T>(x: BuilderInlet<T | null>, k: BuilderInlet<K>): BuilderOutlet<T[K] | undefined>
    get<T, K extends keyof T>(x: BuilderInlet<T | undefined>, k: BuilderInlet<K>): BuilderOutlet<T[K] | undefined>
    get<T, K extends keyof T>(x: BuilderInlet<T | null | undefined>, k: BuilderInlet<K>): BuilderOutlet<T[K] | undefined>
    get<T, K extends keyof T>(x: BuilderInlet<T>, k: BuilderInlet<K>): BuilderOutlet<T[K]> {
        return this.node(GetField, {
            input: x,
            key: k,
        }).output
    }

    private _list<T>(list: BuilderInlet<T>[], localId?: string, sparse?: boolean): BuilderOutlet<T[]> {
        const id = this.fullId(this.checkOrGenLocalId(localId, sparse ? "SparseList" : "List"))
        let connectionData = this.builder.connectionData.get(id)
        if (!connectionData) {
            connectionData = {
                $info: {
                    id,
                    nodeClass: sparse ? SparseList : List,
                },
            }
            ;(connectionData as any).__output_value = {outlet: true, node: connectionData, key: "__output_value", inst: undefined, valueTypeDesc: null}
            this.builder.connectionData.set(id, connectionData)
        }
        const prevLength = (connectionData as any).length
        if (prevLength) {
            for (let n = 0; n < prevLength; n++) {
                ;(connectionData as any)[n] = undefined
            }
        }
        ;(connectionData as any).length = list.length
        for (let n = 0; n < list.length; n++) {
            ;(connectionData as any)[n] = list[n]
        }
        this.builder.unvisitedNodes.delete(id)
        return (connectionData as any).__output_value
    }

    list<T>(list: BuilderInlet<T>[], localId?: string): BuilderOutlet<T[]> {
        return this._list(list, localId)
    }

    sparseList<T>(list: BuilderInlet<T>[], localId?: string): BuilderOutlet<T[]> {
        return this._list(list, localId, true)
    }

    tuple<T extends unknown[]>(...ins: {[K in keyof T]: BuilderInlet<T[K]>}): BuilderOutlet<T> {
        return this.list(ins) as BuilderOutlet<T>
    }

    namedTuple<T extends unknown[]>(uniqueId: NodeId, ...ins: {[K in keyof T]: BuilderInlet<T[K]>}): BuilderOutlet<T> {
        return this.list(ins, uniqueId) as BuilderOutlet<T>
    }

    merge<T>(lists: BuilderInlet<T | T[]>[], localId?: string): BuilderOutlet<T[]> {
        localId = this.checkOrGenLocalId(localId, "Merge")
        const scope = this.scope(localId)
        return scope.node(Merge, {
            input: scope.list(lists),
        }).output
    }

    sparseMerge<T>(lists: BuilderInlet<T | T[]>[], localId?: string): BuilderOutlet<T[]> {
        localId = this.checkOrGenLocalId(localId, "SparseMerge")
        const scope = this.scope(localId)
        return scope.node(Merge, {
            input: scope.sparseList(lists),
        }).output
    }

    flatten<T>(list: BuilderInlet<(T | T[])[]>, localId?: string): BuilderOutlet<T[]> {
        localId = this.checkOrGenLocalId(localId, "Flatten")
        const scope = this.scope(localId)
        return scope.node(Merge, {
            input: list,
        }).output
    }

    map<T, U, Ctx>(
        input: BuilderInlet<T[]>,
        fn: (scope: GraphBuilderScope, elem: T, ctx: BuilderInlet<Ctx>, index: number) => BuilderInlet<U>,
        ctx: BuilderInlet<Ctx>,
        localId?: string,
    ): BuilderOutlet<U[]> {
        localId = this.checkOrGenLocalId(localId, "Map")
        const scope = this.scope(localId)
        return scope.node(MapOverList, {
            input,
            fn,
            ctx,
        }).output
    }

    tap<T>(input: BuilderInlet<T>, fn: (x: T | typeof NotReady) => void): BuilderOutlet<T> {
        return this.node(Tap, {
            input,
            fn,
        }).output
        //TODO: mark root?
    }

    trace<T>(input: BuilderInlet<T>, message?: string): BuilderOutlet<T> {
        let key: string | undefined
        if (!message) {
            if (isBuilderOutlet(input)) {
                key = `${input.node.$info.id}: ${input.key}`
            }
        } else if (isBuilderOutlet(input)) {
            key = `${input.node.$info.id}: ${input.key}: ${message}`
        }
        return this.node(Trace, {
            key: key!,
            input,
            logger: this.builder.logger,
        }).output
    }

    input<T>(key: string): BuilderOutlet<T> {
        this.builder.unvisitedInputs.delete(key)
        let outlet = this.builder.inputs.get(key)
        if (!outlet) {
            outlet = {outlet: true, node: undefined!, key, inst: undefined!}
            this.builder.inputs.set(key, outlet)
        }
        return outlet as BuilderOutlet<T>
    }

    output<T>(key: string, from: BuilderOutlet<T>): void {
        if (from) {
            this.builder.unvisitedOutputs.delete(key)
            this.builder.outputs.set(key, from)
        }
    }

    and(input: BuilderInlet<boolean>[]): BuilderOutlet<boolean> {
        const scope = this.scope(this.genLocalId("And"))
        return scope.node(And, {
            input: scope.list(input),
        }).output
    }

    phi<T>(a: BuilderInlet<T | null>, b: BuilderInlet<T>, ...rest: BuilderInlet<T>[]): BuilderOutlet<T> {
        if (rest.length === 0) {
            return this.node(Phi, {
                a,
                b,
            }).output
        } else {
            const c = rest.shift()!
            return this.phi(a, this.phi(b, c, ...rest))
        }
    }

    branch<T>(input: BuilderInlet<T>): [BuilderOutlet<Exclude<T, null | undefined>>, BuilderOutlet<Extract<T, null | undefined>>] {
        const node = this.node(Branch, {
            input,
        })
        return [node.true, node.false]
    }

    gate<T>(input: BuilderInlet<T>, gate: BuilderInlet<boolean>): BuilderOutlet<T> {
        return this.node(Gate, {
            input,
            gate,
        }).output
    }

    // uniqueId _must_ be supplied for the current scope, because change detection does not work for closure objects and
    // it is assumed that lambda nodes can always be properly matched with previous instances using nodes IDs
    lambda<A, B>(input: BuilderInlet<A>, fn: (x: A) => B, uniqueId: NodeId): BuilderOutlet<B> {
        uniqueId = `Lambda(${uniqueId})`
        return this.node(Lambda, {
            $id: this.fullId(this.ensureUnique(uniqueId)),
            input: input,
            fn: fn,
        }).output
    }

    pureLambda<A, B>(
        input: BuilderInlet<A>,
        fn: (x: A) => B,
        uniqueId: NodeId,
        typeDesc:
            | {
                  input?: ValueTypeDescriptor<A> | undefined
                  output?: ValueTypeDescriptor<B> | undefined
              }
            | undefined = undefined,
    ): BuilderOutlet<B> {
        uniqueId = `PureLambda(${uniqueId})`
        return this.node(generatePureLambda(typeDesc), {
            $id: this.fullId(this.ensureUnique(uniqueId)),
            input: input,
            fn: fn,
        }).output
    }

    const<T>(inputFn: () => T, uniqueId: NodeId): BuilderOutlet<T> {
        return this.node(generatePureLambda<boolean, T>(), {
            $id: this.fullId(this.ensureUnique(uniqueId)),
            input: true,
            fn: inputFn,
        }).output
    }

    holdLast<T>(input: BuilderInlet<T>): BuilderOutlet<T> {
        return this.node(Gate, {
            input,
            gate: true,
        }).output
    }

    // uniqueId _must_ be supplied: all value classes share uniqueName="Value", so a positional id can be
    // reassigned to a value node of a different descriptor across rebuilds, leading to error described in template-input-compile.test.ts
    value<T>(input: BuilderInlet<T>, valueType: ValueTypeDescriptor<T> | undefined, uniqueId: NodeId): BuilderOutlet<T> {
        const valueNodeClass = makeValueNodeClass(valueType)
        return this.node(valueNodeClass, {
            $id: this.fullId(this.ensureUnique(`Value(${uniqueId})`)),
            input,
        }).output
    }

    valueWithoutType<T>(input: BuilderInlet<T>, uniqueId: NodeId): BuilderOutlet<T> {
        const valueNodeClass = makeValueNodeClass<T>(undefined)
        return this.node(valueNodeClass, {
            $id: this.fullId(this.ensureUnique(`Value(${uniqueId})`)),
            input,
        }).output
    }

    filter<T>(input: BuilderInlet<T[]>, pred?: BuilderInlet<(x: T) => boolean>): BuilderOutlet<T[]> {
        return this.node(Filter, {
            input,
            pred: pred,
        }).output
    }

    filterInvalid<T>(input: BuilderInlet<T[]>): BuilderOutlet<Exclude<T, false | null | undefined | 0 | "">[]> {
        return this.node(Filter, {
            input,
            pred: undefined,
        }).output
    }

    entriesToMap<K extends string | number, V>(
        input: BuilderInlet<[K, V][]>,
        valueType: ValueTypeDescriptor<V> | undefined,
        uniqueId: NodeId,
    ): BuilderOutlet<Map<K, V>> {
        return this.node(makeEntriesToMapClass<K, V>(valueType), {
            $id: this.fullId(this.ensureUnique(`EntriesToMap(${uniqueId})`)),
            input: input,
        }).output
    }

    unresolvedToNull<T>(input: BuilderInlet<T | typeof Unresolved>): BuilderOutlet<T | null> {
        return this.node(UnresolvedToNull, {
            input,
        }).output
    }

    waitForFinishedModifyingScene<T>(input: BuilderInlet<T>, sceneManager: ISceneManager) {
        return this.node<WaitForFinishedModifyingSceneDescriptor<T>>(WaitForFinishedModifyingScene, {
            sceneManager,
            input,
        }).output
    }
}
